import { useState, type ReactNode } from 'react';
import { useT } from '@hana/i18n';
import { Button, useToast } from '@hana/ui';
import { IconMap2, IconArrowLeft, IconChevronDown, IconFileText, IconHistory, IconLock, IconRoute, IconUserCircle, IconCalendarEvent, IconChecklist, IconLink, IconShip, IconDots, IconSearch } from '@tabler/icons-react';
import { LTS_WEEKS, cellView } from './lts';
import { BOARD_LTS } from './servicesBoardData';
import { SERVICE_GROUPS, SCHEDULE_STATUS, dedupe, isVnPort, needsSchedule, type ServiceRow } from './svcmap';
import { ServiceMap } from './ServiceMap';
import './ServiceDetail.i18n';
import s from './svcdetail.module.css';

const GROUP_NAME = (k: string) => SERVICE_GROUPS.find((g) => g.key === k)?.label ?? k;

interface Todo { k: string; sub: string; tone: 'warn' | 'err' | 'lb' | 'ok'; act?: string; go?: () => void }

function Acc({ id, open, toggle, icon, title, tip, sum, children }: { id: string; open: boolean; toggle: (id: string) => void; icon: ReactNode; title: string; tip: string; sum: string; children: ReactNode }) {
  const t = useT();
  return (
    <section className={s.card} aria-label={title}>
      <div className={s.cardHead}>
        <span className={s.cardIco} aria-hidden="true">{icon}</span>
        <h2 className={s.h2} title={tip}>{title}</h2>
        <button type="button" className={s.fold} aria-expanded={open} aria-label={t('sd.fold', { v: title.toLowerCase() })} title={t('sd.foldTip')} onClick={() => toggle(id)}>
          <IconChevronDown size={18} aria-hidden="true" className={open ? s.up : undefined} />
        </button>
      </div>
      {open ? children : <div className={s.sum}>{sum}</div>}
    </section>
  );
}

/** Trang chi tiết service (Mint-ServicesMap `vDet`): cột trái Rotation · Hãng · Lịch tàu · Hoạt động; cột phải Việc cần làm · Thông tin chính · Người phụ trách · Liên kết. */
export function ServiceDetail({ v, onBack }: { v: ServiceRow; onBack: () => void }) {
  const t = useT();
  const toast = useToast();
  const [shut, setShut] = useState<ReadonlySet<string>>(new Set(['lnk']));
  const toggle = (id: string) => setShut((c) => { const n = new Set(c); if (n.has(id)) n.delete(id); else n.add(id); return n; });
  const [pt, setPt] = useState<string | null>(null);
  const [menu, setMenu] = useState(false);
  const pending = () => toast({ msg: t('sdt.noWrite'), tone: 'err' });
  const isOpen = (id: string) => !shut.has(id);

  const rot = dedupe(v.rot);
  const vi = Math.max(0, rot.indexOf(v.vn));
  const cells = BOARD_LTS[v.code] ?? [];
  const due = needsSchedule(v);
  const todo: Todo[] = [];
  if (due) todo.push({ k: t('sd.tImport'), sub: `${SCHEDULE_STATUS[v.status]} · ${t('sd.lastTrip', { v: v.last })}`, tone: v.status === 'end' ? 'err' : 'warn', act: t('sd.import'), go: pending });
  if (v.active && v.blank > 0) todo.push({ k: t('sd.tBlank', { n: v.blank }), sub: t('sd.tBlankSub', { v: v.vn }), tone: 'warn', act: t('sd.viewGrid'), go: pending });
  if (!v.link) todo.push({ k: t('sd.tLink'), sub: t('sd.tLinkSub', { v: v.cars[0] ?? '' }), tone: 'lb', act: t('sdt.add'), go: pending });
  todo.push({ k: t('sd.tRecon'), sub: t('sd.tReconSub'), tone: 'lb', act: t('sd.sync'), go: pending });
  if (!due) todo.push({ k: t('sd.tOk'), sub: t('sd.lastTrip', { v: v.last }), tone: 'ok' });
  const nTodo = todo.filter((x) => x.tone !== 'ok').length;

  const info: [string, string][] = [
    [t('sd.iCode'), v.code],
    [t('sd.iName'), v.name],
    [t('sd.iGroup'), `${GROUP_NAME(v.grp)} · Bound ${v.bound} · ${t('sd.vnPortL', { v: v.vn })}`],
    [t('sd.carrier'), v.cars.join(' · ')],
    [t('sd.iLoop'), t('sd.iLoopV', { a: v.circ, b: v.ships })],
    [t('sd.iSched'), `${SCHEDULE_STATUS[v.status]} · ${t('sd.lastImported', { v: v.last })}`],
    [t('sd.iProfile'), v.locked ? t('sd.locked') : t('sd.unlocked')],
  ];
  const hist: [string, string][] = [
    ['22/Oct/2026 10:12', 'Nhập lịch dài hạn · 9 chuyến mới · file hãng'],
    ['15/Oct/2026 09:30', `DAVID · thêm hãng ${v.cars[v.cars.length - 1] ?? ''} vào VSA`],
    ['01/Oct/2026 08:00', 'Đồng bộ lịch tàu thực tế · 3 đổi ETD'],
    ['20/Sep/2026 14:22', v.locked ? 'Khoá hồ sơ service (Locked)' : 'Mở khoá hồ sơ để sửa rotation'],
  ];
  const links: [string, string][] = [[t('sd.lLookup'), '/ezy'], ['Freight Cost', '/ezy/rates'], [t('sd.lInq'), '/ezy/inquiry'], [t('sd.lBooking'), '/ezy/booking'], [t('sd.lSetup'), '/ezy/setup']];
  const pic: [string, string][] = [[t('sd.pPricing'), t('sd.unassigned')], [t('sd.pBkg'), t('sd.unassigned')]];
  const weeks = cells.slice(0, 5).map((raw, i) => ({ w: LTS_WEEKS[i], c: cellView(v.code, raw, i) }));

  return (
    <div className={s.root}>
      <header className={s.head}>
        <button type="button" className={s.back} aria-label={t('sd.back')} title={t('sd.back')} onClick={onBack}>
          <IconArrowLeft size={17} aria-hidden="true" />
        </button>
        <h1 className={s.title}>{v.code}</h1>
        <span className={s.name}>{v.name}</span>
        <span className={`${s.pill} ${s[`l_${v.status}`]}`}><i aria-hidden="true" />{SCHEDULE_STATUS[v.status]}</span>
        {v.locked && (
          <span className={s.lock} title={t('sd.lockedTip')}><IconLock size={12} aria-hidden="true" />{t('sd.lockedProfile')}</span>
        )}
        <span className={s.grow} />
        <span className={s.menuWrap}>
          <button type="button" className={s.more} aria-label={t('sd.moreAct')} title={t('sd.moreAct')} aria-haspopup="menu" aria-expanded={menu} onClick={() => setMenu(!menu)}>
            <IconDots size={17} aria-hidden="true" />
          </button>
          {menu && (
            <div className={s.menu} role="menu" aria-label={t('sd.moreAct')}>
              {[v.locked ? t('sd.mUnlock') : t('sd.mLock'), t('sd.mRename'), t('sd.mDup'), t('sd.mImport')].map((k) => (
                <button key={k} type="button" role="menuitem" className={s.mi} onClick={() => { setMenu(false); pending(); }}>{k}</button>
              ))}
              <button type="button" role="menuitem" className={`${s.mi} ${s.miErr}`} onClick={() => { setMenu(false); pending(); }}>{t('sd.mStop')}</button>
              <div className={s.sep} />
              <a role="menuitem" className={s.mi} href="/ezy"><IconSearch size={16} aria-hidden="true" />{t('sd.lLookup')}</a>
            </div>
          )}
        </span>
        <Button variant="secondary" onClick={pending}>{v.locked ? t('sd.editSched') : t('sd.editSvc')}</Button>
      </header>
      <div className={s.cols}>
        <div className={s.left}>
          <Acc id="smap" open={isOpen('smap')} toggle={toggle} icon={<IconMap2 size={18} stroke={1.9} />} title={t('sd.map')} tip={t('sd.mapTip')} sum={t('sd.mapSum', { c: v.code, n: rot.length })}>
            <ServiceMap code={v.code} rot={rot} circ={v.circ} ships={v.ships} pick={pt} onPick={setPt} />
          </Acc>
          <Acc id="rot" open={isOpen('rot')} toggle={toggle} icon={<IconRoute size={18} stroke={1.9} />} title={t('sd.rotation', { v: v.bound.split(' · ')[0] ?? '' })} tip={t('sd.rotTip')} sum={t('sd.rotSum', { n: rot.length, v: v.vn })}>
            <ol className={s.rot}>
              {rot.map((p, i) => (
                <li key={`${p}-${i}`} className={i === vi || p === pt ? s.rotVn : undefined}>
                  <span className={s.seq}>{i + 1}</span>
                  <b className={isVnPort(p) ? s.vn : undefined}>{p}</b>
                  <span className={s.rotTt}>{i === vi ? t('sd.vnPort') : i > vi && p === v.main ? t('sd.tt', { n: v.tt }) : ''}</span>
                </li>
              ))}
            </ol>
            <div className={s.note}>{t('sd.noLegDays')}</div>
          </Acc>
          <Acc id="cars" open={isOpen('cars')} toggle={toggle} icon={<IconShip size={18} stroke={1.9} />} title={t('sd.carSvc')} tip={t('sd.carSvcTip')} sum={v.cars.join(' · ')}>
            <ul className={s.list}>
              {v.cars.map((c, i) => (
                <li key={c}>
                  <b>{c}</b>
                  <span>{i === 0 ? t('sd.operator') : t('sd.slotBuyer')}</span>
                </li>
              ))}
            </ul>
          </Acc>
          <Acc id="lt" open={isOpen('lt')} toggle={toggle} icon={<IconCalendarEvent size={18} stroke={1.9} />} title={t('sd.sched')} tip={t('sd.schedTip')} sum={t('sd.schedSum', { a: weeks.filter((x) => x.c.kind === 'ok').length, b: v.blank })}>
            <ul className={s.list}>
              {weeks.map(({ w, c }) => (
                <li key={w?.[0]} aria-label={c.al}>
                  <b>{w?.[0]} · {w?.[1]}</b>
                  <span className={c.kind === 'ok' ? undefined : c.kind === 'end' ? s.err : s.warn}>{c.kind === 'ok' ? `${c.label} · ${c.sub}` : `${c.label} · ${c.sub}`}</span>
                </li>
              ))}
            </ul>
          </Acc>
          <Acc id="hist" open={isOpen('hist')} toggle={toggle} icon={<IconHistory size={18} stroke={1.9} />} title={t('sd.activity')} tip={t('sd.activityTip')} sum={t('sd.activitySum', { n: hist.length, v: hist[0]?.[0] ?? '' })}>
            <ul className={s.list}>
              {hist.map(([at, k]) => (
                <li key={at} className={s.col}>
                  <span className={s.at}>{at}</span>
                  <span>{k}</span>
                </li>
              ))}
            </ul>
          </Acc>
        </div>
        <div className={s.right}>
          <Acc id="todo" open={isOpen('todo')} toggle={toggle} icon={<IconChecklist size={18} stroke={1.9} />} title={t('sd.todo')} tip={t('sd.todoTip')} sum={nTodo ? t('sd.todoN', { n: nTodo }) : t('sd.noTodo')}>
            <ul className={s.list}>
              {todo.map((x) => (
                <li key={x.k}>
                  <span className={`${s.tdot} ${s[`t_${x.tone}`]}`} aria-hidden="true" />
                  <span className={s.tx}><b>{x.k}</b><span>{x.sub}</span></span>
                  {x.act && <button type="button" className={s.act} onClick={x.go}>{x.act}</button>}
                </li>
              ))}
            </ul>
          </Acc>
          <Acc id="info" open={isOpen('info')} toggle={toggle} icon={<IconFileText size={18} stroke={1.9} />} title={t('sd.mainInfo')} tip={t('sd.mainInfoTip')} sum={`${v.code} · ${v.cars.join(' · ')} · ${SCHEDULE_STATUS[v.status]}`}>
            <dl className={s.dl}>
              {info.map(([k, val]) => (
                <div key={k}><dt>{k}</dt><dd title={val}>{val}</dd></div>
              ))}
            </dl>
          </Acc>
          <Acc id="pic" open={isOpen('pic')} toggle={toggle} icon={<IconUserCircle size={18} stroke={1.9} />} title={t('sd.owners')} tip={t('sd.ownersTip')} sum={t('sd.unassigned')}>
            <dl className={s.dl}>
              {pic.map(([k, val]) => (
                <div key={k}><dt>{k}</dt><dd className={s.lbv}>{val}</dd></div>
              ))}
            </dl>
          </Acc>
          <Acc id="lnk" open={isOpen('lnk')} toggle={toggle} icon={<IconLink size={18} stroke={1.9} />} title={t('sd.links')} tip={t('sd.linksTip')} sum={t('sd.linksSum', { n: links.length })}>
            <ul className={s.list}>
              {links.map(([k, href]) => (
                <li key={k}><a href={href}>{k}</a></li>
              ))}
            </ul>
          </Acc>
        </div>
      </div>
    </div>
  );
}
